import type { Answers, FieldItem, SurveyContext, Template } from './schema.js';
import { isItemVisible } from './rules.js';
import { isNonCompliant } from './findings.js';

export type IssueCode =
  | 'REQUIRED'
  | 'CONTROL_UNANSWERED'
  | 'NA_NOT_ALLOWED'
  | 'COMMENT_REQUIRED'
  | 'PHOTO_REQUIRED'
  | 'INVALID_OPTION'
  | 'OUT_OF_RANGE'
  | 'INVALID_FORMAT'
  | 'TOO_FEW_ROWS'
  | 'TOO_MANY_ROWS'
  | 'TOO_MANY_FILES'
  | 'CHECKPOINT_REQUIRED';

export interface ValidationIssue {
  sectionCode: string;
  itemId: string;
  /** Field key or control code. */
  ref: string;
  code: IssueCode;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
/** `datetime-local` value ("2026-11-02T14:30"), optionally with seconds and a zone. */
const DATETIME = /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;

/** Field types the reporter does not type in: filled from the site, computed, or set by the template. */
export const DERIVED_FIELD_TYPES = ['customer', 'site', 'branch', 'calculated', 'hidden'] as const;
export function isDerivedField(field: Pick<FieldItem, 'type'>): boolean {
  return (DERIVED_FIELD_TYPES as readonly string[]).includes(field.type);
}

function isRealDate(v: string): boolean {
  const [y, m, d] = v.slice(0, 10).split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m! - 1 && dt.getUTCDate() === d;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

function validateFieldValue(field: FieldItem, value: unknown, push: (code: IssueCode) => void): void {
  if (isEmpty(value)) {
    // Derived fields are filled by the app, never by the reporter: a missing
    // value must not block submission (the server fills lookups on sync).
    if (field.required && !isDerivedField(field)) push('REQUIRED');
    return;
  }
  const optionCodes = new Set(field.options?.map((o) => o.code));
  switch (field.type) {
    case 'single_choice':
      if (!optionCodes.has(value as string)) push('INVALID_OPTION');
      break;
    case 'multi_choice':
      if (!Array.isArray(value) || value.some((v) => !optionCodes.has(v))) push('INVALID_OPTION');
      break;
    case 'number': {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n)) push('INVALID_FORMAT');
      else if ((field.min !== undefined && n < field.min) || (field.max !== undefined && n > field.max)) push('OUT_OF_RANGE');
      break;
    }
    case 'rating': {
      // A rating is a whole number of stars, min..max (default 1..5).
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isNaN(n) || !Number.isInteger(n)) push('INVALID_FORMAT');
      else if (n < ratingRange(field).min || n > ratingRange(field).max) push('OUT_OF_RANGE');
      break;
    }
    case 'time':
      if (typeof value !== 'string' || !TIME.test(value)) push('INVALID_FORMAT');
      break;
    case 'datetime':
      if (typeof value !== 'string' || !DATETIME.test(value) || !isRealDate(value)) push('INVALID_FORMAT');
      break;
    case 'photo':
    case 'file':
      if (!Array.isArray(value) || value.some((v) => typeof v !== 'string')) push('INVALID_FORMAT');
      else if (field.maxPhotos !== undefined && value.length > field.maxPhotos) push('TOO_MANY_FILES');
      break;
    case 'email':
      if (typeof value !== 'string' || !EMAIL.test(value)) push('INVALID_FORMAT');
      break;
    case 'date':
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || !isRealDate(value)) push('INVALID_FORMAT');
      break;
    case 'yes_no':
      if (typeof value !== 'boolean') push('INVALID_FORMAT');
      break;
    case 'repeating_group': {
      if (!Array.isArray(value)) {
        push('INVALID_FORMAT');
        break;
      }
      if (field.minRows !== undefined && value.length < field.minRows) push('TOO_FEW_ROWS');
      if (field.maxRows !== undefined && value.length > field.maxRows) push('TOO_MANY_ROWS');
      for (const row of value as Record<string, unknown>[]) {
        for (const sub of field.fields ?? []) validateFieldValue(sub, row?.[sub.key], push);
      }
      break;
    }
    default:
      break;
  }
}

/** Stars a rating field offers: `min`..`max`, default 1..5. */
export function ratingRange(field: Pick<FieldItem, 'min' | 'max'>): { min: number; max: number } {
  const min = Math.max(0, Math.round(field.min ?? 1));
  const max = Math.max(min, Math.round(field.max ?? 5));
  return { min, max };
}

/**
 * Validate a survey before submission. Hidden items are ignored. Returns an
 * empty list when the survey can be submitted.
 */
export function validateSubmission(template: Template, answers: Answers, context: SurveyContext): ValidationIssue[] {
  const scope = { answers, context };
  const issues: ValidationIssue[] = [];
  for (const section of template.sections) {
    for (const item of section.items) {
      if (!isItemVisible(section, item, scope)) continue;
      if (item.kind === 'field') {
        validateFieldValue(item, answers.fields[item.key], (code) =>
          issues.push({ sectionCode: section.code, itemId: item.id, ref: item.key, code }),
        );
      } else if (item.kind === 'control') {
        const push = (code: IssueCode) => issues.push({ sectionCode: section.code, itemId: item.id, ref: item.code, code });
        const a = answers.controls[item.code];
        if (!a || a.value === null) {
          push('CONTROL_UNANSWERED');
          continue;
        }
        if (a.value === 'NA' && !item.allowNA) push('NA_NOT_ALLOWED');
        const hasNote = !!a.note && a.note.trim().length > 0;
        const nonCompliant = isNonCompliant(item, a.value);
        if (nonCompliant && item.requireCommentOn.includes('NON_COMPLIANT') && !hasNote) push('COMMENT_REQUIRED');
        if (a.value === 'NA' && item.requireCommentOn.includes('NA') && !hasNote) push('COMMENT_REQUIRED');
        if (nonCompliant && item.requirePhotoOn.includes('NON_COMPLIANT') && a.photos.length === 0) push('PHOTO_REQUIRED');
      }
    }
  }
  return issues;
}

/** Progress for the capture UI: answered / total visible questions. */
export function progress(template: Template, answers: Answers, context: SurveyContext): { answered: number; total: number } {
  const scope = { answers, context };
  let answered = 0;
  let total = 0;
  for (const section of template.sections) {
    for (const item of section.items) {
      if (item.kind === 'layout' || !isItemVisible(section, item, scope)) continue;
      // Only questions the reporter answers count; derived fields fill themselves.
      if (item.kind === 'field' && isDerivedField(item)) continue;
      total++;
      const v = item.kind === 'field' ? answers.fields[item.key] : answers.controls[item.code]?.value;
      if (!isEmpty(v)) answered++;
    }
  }
  return { answered, total };
}

/**
 * Proof of presence (ADR-0034): every visible control whose tag is REQUIRED
 * needs a verified scan. `verified` holds the control codes the server
 * verified (on the device: scans pending or verified).
 */
export function checkpointIssues(template: Template, answers: Answers, context: SurveyContext, verified: ReadonlySet<string>): ValidationIssue[] {
  const scope = { answers, context };
  const out: ValidationIssue[] = [];
  for (const section of template.sections)
    for (const item of section.items)
      if (item.kind === 'control' && item.checkpoint === 'REQUIRED' && isItemVisible(section, item, scope) && !verified.has(item.code))
        out.push({ sectionCode: section.code, itemId: item.id, ref: item.code, code: 'CHECKPOINT_REQUIRED' });
  return out;
}
